import { Component, OnInit, input } from '@angular/core';
import { faWarning, faExternalLink } from '@fortawesome/free-solid-svg-icons';
import { faCircleXmark } from '@fortawesome/free-regular-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { ReportService } from 'src/app/services/report.service';
import { ConfirmationToastComponent } from 'src/app/components/confirmation-toast/confirmation-toast.component';
import { FormBuilder, FormControl } from '@angular/forms';
import { Validators } from 'ngx-editor';
import { ViewFullSizeImageModalComponent } from './view-full-size-image/view-full-size-image.component';
import { MatDialog } from '@angular/material/dialog';

type ThumbnailInfo = {
  image?: string,
  supporting_files_id?: number,
  name: string,
  size: number,
  extension: string,
  control?: FormControl,
  progress?: number,
  canceled?: boolean,
  caption?: string,
}

@Component({
    selector: 'view-image',
    templateUrl: './view-image.component.html',
    styleUrls: ['./view-image.component.scss'],
    standalone: false
})


export class ViewImageComponent implements OnInit {

  faExternalLink = faExternalLink;

  section = input.required<string>();
  question = input.required<string>();
  supporting_files = input.required<any>();

  thumbnails: any[] = [];

  fileTypeMatch = /^.*\.(bmp|tiff|png|jpeg|jpg|gif|x-ms-bmp)$/;

  constructor(
    public dialog: MatDialog,
    public readonly reportService: ReportService,
    private readonly toast: ConfirmationToastComponent,
    private readonly formBuilder: FormBuilder,
  ) { }

  ngOnInit(): void {
    this.supporting_files().forEach((fileInfo: any) => {
      if (fileInfo.section == this.section() && fileInfo.question == this.question()) {
        this.loadThumbnail(fileInfo)
      }
    })
  }

  /**
   * Load thumbnail from database. Not using url directly as we need authentication.
   */
  loadThumbnail(fileInfo: any, thumbnailPlaceholder?: ThumbnailInfo) {
    this.reportService.getFullSizeImage(fileInfo.supporting_files_id).subscribe({
      next: (response) => {
        this.convertBlobToBase64(response).then((base64) => { //instead of url so print pdf functions
          if (thumbnailPlaceholder) {
            thumbnailPlaceholder.supporting_files_id = fileInfo.supporting_files_id;
            if (fileInfo.name) {
              thumbnailPlaceholder.name = fileInfo.file_name.substring(37) // remove extra characters from name
            }
            thumbnailPlaceholder.progress = 1
            thumbnailPlaceholder.control = this.formBuilder.control("", Validators.maxLength(150))
            setTimeout(() => {
              thumbnailPlaceholder.image = base64;
              thumbnailPlaceholder.progress = undefined
            }, 1000)
          } else {
            let fileName = fileInfo.file_name.substring(37) // remove extra characters from name
            let item: ThumbnailInfo = {
              image: base64,
              supporting_files_id: fileInfo.supporting_files_id,
              name: fileName,
              size: fileInfo.image_size,
              extension: this.getFileExtension(fileName),
              control: this.formBuilder.control(fileInfo.caption ?? "", Validators.maxLength(150)),
              caption: fileInfo.caption,
            }
            this.thumbnails.push(item)
          }
        })
      },
      error: (response) => {
        console.log("err", response)
        let fileName = ""
        if (thumbnailPlaceholder) {
          fileName = thumbnailPlaceholder.name
          thumbnailPlaceholder.progress = undefined
        } else {
          fileName = fileInfo.file_name.substring(37) // remove extra characters from name
        }
        this.toast.openErrorSnackBar(`There was an error displaying file ${fileName}`);
      },
    })
  }

  convertBlobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(new Error('Error reading file.'));
      reader.readAsDataURL(blob); // Converts Blob to Base64
    });
  }

  getFileExtension(fileName: string): string {
    return (/(?:\.([^.]+))?$/.exec(fileName) ?? "")[1].toString().toUpperCase()
  }

  viewFullSizedImage(event: any, thumbnail: ThumbnailInfo): void {
    event.stopPropagation();
    this.dialog.open(ViewFullSizeImageModalComponent, {
      width: "700px",
      panelClass: ['animate__animated', 'animate__fadeInDown'],
      data: {
        thumbnail: thumbnail
      },
    })
  }
}
